import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, useFormState } from 'react-final-form';
import {
  DateInput,
  SelectInput,
  TextInput,
  required,
  regex,
  useDataProvider,
  useNotify,
  useRefresh,
  useUpdate,
} from 'react-admin';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@material-ui/core';

import { nationalities, normalizeSilaeId, parseDate } from '../../../helpers';

const validateEmail = regex(
  /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/,
  'Email invalide',
);

const validatePhoneNumber = regex(
  /^((\+33\s?[1-9](?:\s?\d{2}){4})|(0[1-9](?:\s?\d{2}){4}))$/,
  'Numéro de téléphone invalide',
);

const SALUTATION_CHOICES = [
  { id: 'Mr.', name: 'Mr.' },
  { id: 'Mrs.', name: 'Mrs.' },
];

// The identity fields this dialog owns (plan vivier — edit identité). Kept as
// an explicit allow-list so both the getOne pre-fill and the cleaner PATCH
// stay scoped to exactly these columns: we never spread the whole `cleaner`
// row, so a read-only/unrelated column (silae_id, employee_status, fullname,
// country, social_security_number…) never gets echoed back into the update.
const IDENTITY_FIELDS = [
  'salutation',
  'firstname',
  'lastname',
  'email',
  'mobile_phone',
  'phone',
  'birthdate',
  'birthplace',
  'nationality',
  'mailing_street',
  'mailing_additional',
  'mailing_postal_code',
  'mailing_city',
];

const pickIdentityFields = (source) => {
  return IDENTITY_FIELDS.reduce((acc, field) => {
    acc[field] = source && source[field] !== undefined ? source[field] : null;
    return acc;
  }, {});
};

// A stale server error must not resurface once the user has edited the form
// since the refusal (see handleSubmit's mutual-exclusion comment below): the
// `!invalid` guard only hides it while a field is broken, it doesn't clear
// the state, so fixing that field brings the old message back even though no
// new submission happened. `dirtySinceLastSubmit` (react-final-form) flips to
// true the moment any value differs from what it was at the last submit()
// call — including one blocked by client validation, since that also counts
// as "the last submit" — so it fires for both edit-after-refusal and
// edit-after-blocked-attempt. Has to be its own component (not code inline in
// the render prop below) to call react-final-form's own hooks — same pattern
// as HireDialog's SeniorityDefaultSync.
const ClearStaleServerError = ({ onDirty }) => {
  const { dirtySinceLastSubmit } = useFormState({
    subscription: { dirtySinceLastSubmit: true },
  });
  useEffect(() => {
    if (dirtySinceLastSubmit) {
      onDirty(null);
    }
  }, [dirtySinceLastSubmit, onDirty]);
  return null;
};

ClearStaleServerError.propTypes = {
  onDirty: PropTypes.func.isRequired,
};

// "Modifier l'identité" — edits the identity portion of a single vivier fiche.
// Scope = this fiche only, same as Supprimer la fiche / Embaucher (Show.js):
// a silae matricule commonly spans several `cleaner` fiches (522/952 measured
// in prod), and this dialog deliberately does not fan the edit out to sibling
// fiches.
//
// care.person_vivier is a read-only view (SELECT-only grant, no INSTEAD rule)
// — writes always go through the `cleaner` resource, whose UPDATE is
// rewritten server-side (care_cleaner_update rule, `WHERE sfid = old.id`) onto
// the real identity table, keyed on `record.id` (every vivier fiche carries an
// sfid). A server-side control (checkCleaner) may reject the write (date
// coherence, age, etc.) — its message is surfaced as-is, see handleSubmit.
//
// person_vivier does not expose salutation/mailing_street/mailing_additional/
// mailing_postal_code/birthplace, so the `record` injected by <Show> is
// missing them: on open we re-fetch the full `cleaner` row via getOne to
// pre-fill the form (loading/error state wraps that fetch).
const EditIdentityDialog = ({ open, onClose, record }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [update, { loading: saving }] = useUpdate();

  const [source, setSource] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  const cleanerId = record && record.id;

  useEffect(() => {
    if (!open || !cleanerId) {
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setSource(null);
    setSubmitError(null);
    dataProvider
      .getOne('cleaner', { id: cleanerId })
      .then(({ data }) => {
        if (!cancelled) setSource(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(
            (err && err.message) || 'Échec du chargement de la fiche',
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, cleanerId, dataProvider]);

  if (!record) {
    return null;
  }

  const matricule = normalizeSilaeId(record.silae_id);
  const scopeHint = matricule
    ? `Ne modifie que cette fiche (matricule ${matricule}) — les éventuelles autres fiches du même salarié ne sont pas mises à jour.`
    : 'Ne modifie que cette fiche — les éventuelles autres fiches du même salarié ne sont pas mises à jour.';

  const handleSubmit = (values) => {
    setSubmitError(null);
    const data = { id: cleanerId, ...pickIdentityFields(values) };
    return update('cleaner', cleanerId, data, source || record, {
      onSuccess: () => {
        notify('Identité mise à jour', 'info');
        onClose();
        refresh();
      },
      onFailure: (err) => {
        const message =
          typeof err === 'string'
            ? err
            : (err && err.message) || "Échec de la mise à jour de l'identité";
        setSubmitError(message);
        notify(message, 'warning');
      },
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Modifier l&apos;identité</DialogTitle>
      {/* Sibling of DialogTitle/DialogContent, not a DialogContent itself:
          MUI v4's DialogContent is `flex: 1 1 auto` + `overflow-y: auto` —
          stacking two of them makes them compete for height and squashes the
          first (measured 16px for 36px of text). Same subtitle pattern as
          Cleaner/detail/CreateCleanerDialog.js. */}
      <Typography
        variant="body2"
        color="textSecondary"
        style={{ padding: '0 24px 16px' }}
      >
        {scopeHint}
      </Typography>
      {loading && (
        <DialogContent>
          <Grid container justify="center" style={{ padding: 24 }}>
            <CircularProgress size={24} />
          </Grid>
        </DialogContent>
      )}
      {!loading && loadError && (
        <>
          <DialogContent>
            <Typography color="error">{loadError}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Fermer</Button>
          </DialogActions>
        </>
      )}
      {!loading && !loadError && source && (
        <Form
          onSubmit={handleSubmit}
          initialValues={pickIdentityFields(source)}
          subscription={{ invalid: true, submitting: true, submitFailed: true }}
          render={({ handleSubmit: rffSubmit, invalid, submitFailed }) => {
            return (
              // `.MuiDialog-paper` is `display: flex; flex-direction: column;
              // max-height: calc(100% - 64px)`, and relies on DialogContent
              // (flex: 1 1 auto + overflow-y: auto from its own MUI class)
              // being its DIRECT flex child to become the scrolling region
              // while DialogActions stays pinned at the bottom. Here `<form>`
              // sits in between, breaking that chain: without its own flex
              // layout it takes its natural (unconstrained) height, so with
              // 13 fields DialogActions gets pushed below the paper's — and
              // on a short viewport, the screen's — visible area, with
              // nothing left to scroll (same latent defect in HireDialog,
              // masked there by its 5-field form staying under the cap).
              // `minHeight: 0` overrides the flex-item default of
              // `min-height: auto`, which would otherwise refuse to let this
              // node shrink below its content's natural size — the one line
              // that actually makes DialogContent's overflow-y: auto kick in.
              <form
                onSubmit={rffSubmit}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  maxHeight: '100%',
                }}
              >
                <ClearStaleServerError onDirty={setSubmitError} />
                <DialogContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={2}>
                      <SelectInput
                        fullWidth
                        source="salutation"
                        label="Civilité"
                        choices={SALUTATION_CHOICES}
                        allowEmpty
                        emptyText="ra.action.clear_input_value"
                      />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <TextInput
                        fullWidth
                        source="firstname"
                        label="Prénom"
                        validate={[required()]}
                      />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <TextInput
                        fullWidth
                        source="lastname"
                        label="Nom"
                        validate={[required()]}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextInput
                        fullWidth
                        source="email"
                        label="E-mail"
                        validate={[validateEmail]}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextInput
                        fullWidth
                        source="mobile_phone"
                        label="Mobile"
                        validate={[validatePhoneNumber]}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextInput
                        fullWidth
                        source="phone"
                        label="Téléphone"
                        validate={[validatePhoneNumber]}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <DateInput
                        fullWidth
                        source="birthdate"
                        label="Naissance"
                        parse={parseDate}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextInput
                        fullWidth
                        source="birthplace"
                        label="Lieu de naissance"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <SelectInput
                        fullWidth
                        source="nationality"
                        label="Nationalité"
                        choices={nationalities}
                        allowEmpty
                        emptyText="ra.action.clear_input_value"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextInput
                        fullWidth
                        source="mailing_street"
                        label="Adresse"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextInput
                        fullWidth
                        source="mailing_additional"
                        label="Complément d'adresse"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextInput
                        fullWidth
                        source="mailing_postal_code"
                        label="Code postal"
                      />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <TextInput
                        fullWidth
                        source="mailing_city"
                        label="Ville"
                      />
                    </Grid>
                  </Grid>
                </DialogContent>
                {/* Anchored between DialogContent and DialogActions — a
                    sibling flex item of the `<form>`'s column layout above,
                    not part of the scrollable DialogContent — so it stays
                    visible regardless of scroll position. A field-level
                    error can be scrolled out of view on a form this long, and
                    that's exactly the situation this banner exists to cover;
                    putting it inside DialogContent (as a previous version of
                    this file did) defeated that purpose.
                    Enregistrer is never disabled for invalidity (only while
                    saving) — see the button below. A click while invalid
                    goes through final-form's own submit(): it blocks the
                    onSubmit call, calls markAllFieldsTouched() (which is what
                    makes each faulty TextInput/SelectInput/DateInput show its
                    own red state — they gate on `touched`, see
                    ra-ui-materialui/input/*.tsx) and sets submitFailed —
                    hence this message needing only `invalid`+`submitFailed`
                    in the Form's subscription, no extra state. Mutually
                    exclusive with the server error below by construction:
                    handleSubmit (and so a fresh submitError) can only ever
                    run once the form is client-valid, i.e. `!invalid`. */}
                {invalid && submitFailed ? (
                  <div style={{ padding: '8px 24px' }}>
                    <span style={{ color: '#f44336' }}>
                      Certains champs sont invalides — corrigez les champs
                      signalés en rouge.
                    </span>
                  </div>
                ) : null}
                {!invalid && submitError ? (
                  <div style={{ padding: '8px 24px' }}>
                    <span style={{ color: '#f44336' }}>{submitError}</span>
                  </div>
                ) : null}
                <DialogActions>
                  <Button onClick={onClose} disabled={saving}>
                    Annuler
                  </Button>
                  <Button
                    type="submit"
                    color="primary"
                    variant="contained"
                    disabled={saving}
                    startIcon={
                      saving ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : null
                    }
                  >
                    Enregistrer
                  </Button>
                </DialogActions>
              </form>
            );
          }}
        />
      )}
    </Dialog>
  );
};

EditIdentityDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  record: PropTypes.object,
};

EditIdentityDialog.defaultProps = {
  record: undefined,
};

export default EditIdentityDialog;
